// Landing-page interaction hooks.
//
// Nothing in here is motion for its own sake: each hook exists because a
// number, a card or a heading reads better when it arrives than when it is
// simply already there. Every one of them checks the reader's motion
// preference, so the whole page calms down together rather than in patches.
import { useEffect, useRef, useState } from 'react';

export function reducedMotion() {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Sections arrive as you reach them. Observed by attribute rather than by ref
// so a section only has to say `data-reveal` to join in — no wiring per node.
export function useReveal(scopeRef) {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return;
    const nodes = scope.querySelectorAll('[data-reveal]');
    if (reducedMotion()) {
      for (const node of nodes) node.dataset.revealed = 'yes';
      return;
    }
    const seen = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.dataset.revealed = 'yes';
        seen.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });
    for (const node of nodes) seen.observe(node);
    return () => seen.disconnect();
  }, [scopeRef]);
}

// How far down the page you are, as a rust-coloured seam across the top. It
// doubles as the only chrome that never leaves, so it is also where the neon
// switch and the shell hint live.
export function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const doc = document.documentElement;
      const span = doc.scrollHeight - doc.clientHeight;
      setProgress(span > 0 ? Math.min(1, doc.scrollTop / span) : 0);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      removeEventListener('scroll', onScroll);
      removeEventListener('resize', onScroll);
    };
  }, []);
  return progress;
}

// A figure that counts itself up the first time you look at it. It starts only
// when the number is on screen, so the reader sees the whole run rather than
// walking in on a finished total.
export function useCountUp(target, { duration = 1500 } = {}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(() => (reducedMotion() ? target : 0));
  useEffect(() => {
    const node = ref.current;
    if (!node || reducedMotion()) { setShown(target); return; }
    let frame = 0;
    const watcher = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      watcher.disconnect();
      const started = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - started) / duration);
        // easeOutExpo: it sprints, then lands. A linear count reads like a
        // progress bar; this reads like an odometer coming to rest.
        const eased = t === 1 ? 1 : 1 - 2 ** (-10 * t);
        setShown(Math.round(target * eased));
        if (t < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    }, { threshold: 0.4 });
    watcher.observe(node);
    return () => { watcher.disconnect(); cancelAnimationFrame(frame); };
  }, [target, duration]);
  return [ref, shown];
}

// Pointer-lit surfaces. The card itself does the drawing in CSS off two custom
// properties; this only reports where the pointer is, which keeps the work off
// the main thread and out of React's render path.
export function useSpotlight(scopeRef, selector) {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || reducedMotion()) return;
    const onMove = (event) => {
      const card = event.target.closest?.(selector);
      if (!card || !scope.contains(card)) return;
      const box = card.getBoundingClientRect();
      const x = (event.clientX - box.left) / box.width;
      const y = (event.clientY - box.top) / box.height;
      card.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
      card.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
      card.style.setProperty('--tilt-x', `${((0.5 - y) * 7).toFixed(2)}deg`);
      card.style.setProperty('--tilt-y', `${((x - 0.5) * 9).toFixed(2)}deg`);
    };
    const onLeave = (event) => {
      const card = event.target.closest?.(selector);
      if (!card) return;
      card.style.setProperty('--tilt-x', '0deg');
      card.style.setProperty('--tilt-y', '0deg');
    };
    scope.addEventListener('pointermove', onMove);
    scope.addEventListener('pointerout', onLeave);
    return () => {
      scope.removeEventListener('pointermove', onMove);
      scope.removeEventListener('pointerout', onLeave);
    };
  }, [scopeRef, selector]);
}

// The wordmark oxidises: hover it and the letters corrode through a run of
// junk before settling back. Rust, as a verb.
const CORRODE = '#%&$@01/\\<>*+=~';
export function useScramble(text) {
  const [shown, setShown] = useState(text);
  const timer = useRef(0);
  const start = () => {
    if (reducedMotion() || timer.current) return;
    const chars = [...text];
    let tick = 0;
    timer.current = setInterval(() => {
      tick += 1;
      setShown(chars.map((char, index) => (
        // Letters settle left to right, two ticks apart, so the word resolves
        // instead of blinking back all at once.
        index * 2 < tick ? char : CORRODE[Math.floor(Math.random() * CORRODE.length)]
      )).join(''));
      if (tick > chars.length * 2 + 2) {
        clearInterval(timer.current);
        timer.current = 0;
        setShown(text);
      }
    }, 40);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  return [shown, start];
}

// Up up down down left right left right B A — and the kernel gives up.
const KONAMI = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
export function useKonami(onUnlock) {
  useEffect(() => {
    let at = 0;
    const onKey = (event) => {
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      at = key === KONAMI[at] ? at + 1 : (key === KONAMI[0] ? 1 : 0);
      if (at === KONAMI.length) { at = 0; onUnlock(); }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [onUnlock]);
}
